import {gsap} from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import {createShadowElementForTrigger} from "./Utils";
import {computeInStartTrigger, computeStickyUntilSceneEnd, readCanvasSticky} from "./AnimationUtils";

/**
 * Canvas sticky scrub: the range follows the nearest canvas-sticky element — the target itself or a
 * (grand)parent group — with the same "On sticky" settings as a sticky scroll animation (onSticky,
 * durationUnit, scrubDuration). Anchor, margin and sticky duration are read from the classes and
 * CSS vars the publish renders on the sticky element, so they follow the active layout and every
 * ScrollTrigger.refresh() re-reads them. The sticky element moves while pinned, so the trigger is
 * its static shadow, as for sticky animations.
 * Returns null when no canvas-sticky element is found (e.g. not sticky in this layout).
 */
function createCanvasStickyRange (target, scrubItem, sceneElement, spread) {
    const stickyEl = target.closest('.canvas-sticky')
    if (stickyEl == null || !sceneElement.contains(stickyEl)) {
        return null
    }

    const measure = () => ({
        onSticky: scrubItem.onSticky ?? 'start',
        durationUnit: scrubItem.durationUnit ?? 'pixel',
        scrubDuration: scrubItem.scrubDuration ?? 0,
        ...readCanvasSticky(stickyEl.classList, window.getComputedStyle(stickyEl)),
    })
    const sceneScale = () => parseFloat(sceneElement.style.getPropertyValue('--sceneScale')) || 1
    const range = {
        trigger: createShadowElementForTrigger(stickyEl, sceneElement, { direction: 'scrub' }),
        start: () => computeInStartTrigger(measure(), sceneScale(), spread.getInterfaceOffset()),
    }

    const { durationUnit, stickyForPx } = measure()
    if (durationUnit === 'sticky' && !(stickyForPx > 0)) {
        // No sticky duration: the element sticks until the scene ends.
        range.endTrigger = sceneElement
        range.end = () => computeStickyUntilSceneEnd(measure(), stickyEl.offsetHeight, sceneScale(), spread.getInterfaceOffset())
    } else if (durationUnit === 'sticky') {
        range.end = () => `+=${measure().stickyForPx * sceneScale()}`
    } else if (durationUnit === 'percentage') {
        range.end = `+=${scrubItem.scrubDuration ?? 0}%`
    } else {
        range.end = () => `+=${(scrubItem.scrubDuration ?? 0) * sceneScale()}`
    }
    return range
}

/**
 * Stack sticky scrub (ADR 0046): the range is the pin travel of the sticky element within its
 * rail (its direct parent) — progress 0 the moment it pins, 1 when the rail bottom releases it.
 * A pinned element is an unreliable ScrollTrigger target (it moves while stuck), so the rail is
 * the trigger. Measurements neutralise the pinning temporarily so rects reflect static flow, and
 * run inside the start/end functions so every ScrollTrigger.refresh() re-measures.
 * Returns null when no sticky element is found (sticky was disabled after authoring).
 */
function createStickyRailRange (target, sceneElement) {
    let stickyEl = target
    while (stickyEl && stickyEl !== sceneElement) {
        if (window.getComputedStyle(stickyEl).position === 'sticky') break
        stickyEl = stickyEl.parentElement
    }
    if (stickyEl == null || stickyEl === sceneElement) {
        return null
    }
    const rail = stickyEl.parentElement
    if (rail == null) {
        return null
    }

    const measure = () => {
        const previousPosition = stickyEl.style.position
        stickyEl.style.position = 'relative' // neutralise pinning: rects reflect static flow
        const railRect = rail.getBoundingClientRect()
        const elRect = stickyEl.getBoundingClientRect()
        stickyEl.style.position = previousPosition
        const stickyStyle = window.getComputedStyle(stickyEl)
        const stickyTop = parseFloat(stickyStyle.top)
        const stickyBottom = parseFloat(stickyStyle.bottom)
        const topInRail = elRect.top - railRect.top
        return {
            topInRail,
            elementHeight: elRect.height,
            stickyTop: Number.isFinite(stickyTop) ? stickyTop : 0,
            stickyBottom: Number.isFinite(stickyBottom) ? stickyBottom : 0,
            pinsToBottom: !Number.isFinite(stickyTop) && Number.isFinite(stickyBottom),
            travel: Math.max(railRect.height - elRect.height - topInRail, 0),
        }
    }

    return {
        trigger: rail,
        start: () => {
            const { topInRail, elementHeight, stickyTop, stickyBottom, pinsToBottom } = measure()
            if (pinsToBottom) {
                return `top+=${topInRail + elementHeight} bottom-=${stickyBottom}`
            }
            return `top+=${topInRail} top+=${stickyTop}`
        },
        end: () => `+=${measure().travel}`,
    }
}

export default class Scrub {
    constructor(scene, scrubs) {
        this.scene = scene;
        this.scrubs = scrubs;
        this.triggers = [];
        this.enabled = false;
        this.webcodecPlugin = null;
        this.destroyed = false;
        this.animationFrames = new Set();
    }

    async init() {
        if (this.scrubs == null) {
            return
        }

        if (this.scrubs.some(scrub => scrub.type === 'video')) {
            import(/* webpackChunkName: "webcodec" */ '../../submodules/scrub-video-webcodec').then(async ({ MaglrVideoEnhancementPlugin }) => {
                if (this.destroyed) return;
                // The manager's create() returns a global singleton. Each scene needs
                // its own plugin so disposal cannot stop another visible scene.
                const plugin = new MaglrVideoEnhancementPlugin({userErrors: {enabled: this.scene.spread.isEditorPreview()}});
                this.webcodecPlugin = plugin;
                await plugin.startVideoEnhancement();
                if (this.destroyed) {
                    plugin.destroy();
                    return;
                }
                const videoIds = this.scrubs.filter(entry => entry.type === 'video').filter(entry => {
                    const node = this.scene.runSelector(`#${entry.uuid}`)[0];
                    // The plugin's ID observer currently searches the document, not
                    // the outer spread shadow root. Never attach to an ID alias.
                    if (!node || document.getElementById(entry.uuid) !== node) {
                        console.warn('[Scrub] Video enhancement cannot locate the owned scene node:', entry.uuid);
                        return false;
                    }
                    return true;
                }).map(entry => entry.uuid);
                if (videoIds.length) plugin.observe(videoIds);
            }).catch(error => console.error('An error occurred while loading the component', error));
        }

        for (const scrubItem of this.scrubs) {
            const queryUuid = `#${scrubItem.uuid}`
            const target = this.scene.runSelector(queryUuid)[0]
            if (target == null) {
                continue // go to next iteration if no target is found
            }

            const stickyEnabled = target.classList.contains('grid-sticky')
            const measureViewport = () => {
                const style = window.getComputedStyle(target);
                return {
                    viewHeight: this.scene.spread.getWindowHeight(),
                    stickyMargin: style.getPropertyValue('--sticky-margin-px'),
                    stickyScrub: style.getPropertyValue('--sticky-for') * this.scene.getSizeData().scale,
                };
            };
            let triggerTarget = stickyEnabled ? createShadowElementForTrigger(target, this.scene.getSceneElement(false), { direction: 'scrub' }) : target

            // Stack sticky range mode: trigger on the sticky rail instead of authored start/end points.
            let stickyRailRange = null
            if (scrubItem.mode === 'sticky') {
                stickyRailRange = createStickyRailRange(target, this.scene.getSceneElement(false))
                if (stickyRailRange == null) {
                    // Sticky was removed after authoring: degrade to the default viewport span.
                    scrubItem.start = scrubItem.start ?? 'bottom 100%'
                    scrubItem.end = scrubItem.end ?? 'top 0%'
                }
            }

            // Canvas sticky range mode: trigger on the nearest canvas-sticky element (target or ancestor).
            let canvasStickyRange = null
            if (scrubItem.mode === 'canvasSticky') {
                canvasStickyRange = createCanvasStickyRange(target, scrubItem, this.scene.getSceneElement(false), this.scene.spread)
                if (canvasStickyRange == null) {
                    // Not sticky (in this layout): scrub over the published viewport range, else the default span.
                    scrubItem.start = scrubItem.start ?? 'bottom 100%'
                    scrubItem.end = scrubItem.end ?? 'top 0%'
                }
            }
            const range = stickyRailRange ?? canvasStickyRange

            switch (scrubItem.type) {
                case 'video': {
                    const duration = target.getAttribute('data-duration')
                    scrubItem.to = {...scrubItem.to, currentTime: duration - 0.5 || 0 } // scrub to half a second less than the full duration, to prevent it jump back to the first frame
                } break
                case 'slideshow': {
                    scrubItem.onUpdate = (self) => {
                        const count = target.getAttribute('data-slidecount')
                        const index = Math.round(self.progress * count)
                        this.scene.slideshows?.doAction(target, index)
                    }
                } break
                // case 'chart': {
                //     scrubItem.onUpdate = (self) => {
                //         this.scene.charts?.updateScrub(queryUuid, self.progress)
                //     }
                // } break
                case 'webcomponent': {
                    scrubItem.onUpdate = (self) => {
                        // Start a loop that checks the animation progress
                        const checkProgress = () => {
                            if (this.destroyed) return;
                            const currentProgress = self.animation.progress();
                            this.seek(queryUuid, currentProgress);
                            if (Math.abs(currentProgress - self.progress) > 0.001) {
                                const frame = requestAnimationFrame(() => {
                                    this.animationFrames.delete(frame);
                                    checkProgress();
                                });
                                this.animationFrames.add(frame);
                            }
                        };
                        checkProgress();
                    }
                } break
                default: {
                } break
            }

            const scrubStart = () => {
                const { viewHeight, stickyMargin, stickyScrub } = measureViewport();
                const startMatch = scrubItem.start.match(/(top|center|bottom)\s+(\d+)%/);
                if (startMatch) {
                    const startDirection = startMatch[1];
                    const startPercentage = parseInt(startMatch[2], 10) * 0.01;
                    const startOffset = startDirection === 'bottom' ? target.offsetHeight : startDirection === 'center' ? target.offsetHeight * 0.5 : 0
                    if (viewHeight * (1 - startPercentage) + startOffset > viewHeight - stickyMargin) {
                        return `clamp(${scrubItem.start}-=${stickyScrub})`
                    }
                }
                return `clamp(${scrubItem.start})`
            }
            const scrubEnd = () => {
                const { viewHeight, stickyMargin, stickyScrub } = measureViewport();
                const endMatch = scrubItem.end.match(/(top|center|bottom)\s+(\d+)%/);
                if (endMatch) {
                    const endDirection = endMatch[1];
                    const endPercentage = parseInt(endMatch[2], 10) * 0.01;
                    const endOffset = endDirection === 'top' ? target.offsetHeight : endDirection === 'center' ? target.offsetHeight * 0.5 : 0
                    if (viewHeight * (1 - endPercentage) + endOffset < viewHeight - stickyMargin - target.offsetHeight) {
                        return scrubItem.end
                    }
                }
                if (scrubItem.mode === 'viewport') {
                    return `${scrubItem.end}-=${stickyScrub}`
                }
                return scrubItem.end;
            }
            const animationTargetUuid = scrubItem.type === 'video' ? `#video_${scrubItem.uuid}-${this.scene.spread.spread_id}` : queryUuid;
            const animationTarget = this.scene.runSelector(animationTargetUuid)[0]
            const animation = gsap.fromTo(scrubItem.to ? animationTarget : { progress: 0 }, scrubItem.from, scrubItem.to ?? { progress: 100 })
            const scrubSettings = {
                scroller: this.scene.spread.getAnimationScroller(),
                trigger: range ? range.trigger : triggerTarget,
                start: range ? range.start : scrubStart,
                end: range ? range.end : scrubEnd,
                endTrigger: range?.endTrigger,
                scrub: scrubItem.scrub,
                onUpdate: scrubItem.onUpdate,
                animation: animation,
                markers: false,
                id: scrubItem.type,
            }


            const scrollTrigger = ScrollTrigger.create(scrubSettings)
            if (!this.enabled) {
                scrollTrigger.disable()
            }
            this.triggers.push(scrollTrigger)
        }
    }

    stop() {
        this.disableAll()
    }

    destroy() {
        if (this.destroyed) return;
        this.destroyed = true;
        this.animationFrames.forEach(frame => cancelAnimationFrame(frame));
        this.animationFrames.clear();
        this.triggers.forEach(trigger => trigger.kill());
        this.triggers = [];
        if (this.webcodecPlugin) {
            this.webcodecPlugin.destroy();
            this.webcodecPlugin = null;
        }
    }

    refresh() {
        this.triggers.forEach(trigger => trigger.refresh());
    }

    enableAll() {
        this.enabled = true;
        this.triggers.forEach(trigger => trigger.enable())
    }

    disableAll() {
        this.enabled = false;
        this.triggers.forEach(trigger => trigger.disable())
    }

    seek(uuid, progress) {
        const elements = this.scene.runSelector(uuid);
        const el = elements?.[0];
        const child = el?.children?.[0];
        if (typeof child?.seek === 'function') {
            child.seek(progress);
        }
    }
}
